//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import {
  UnifiedMetricsCampaignsStatsResponse,
  UnifiedMetricsMetadataResponse,
} from '@bvengineering/metrics-gateway-service-types';

import { ITrackingParamGroup } from '@legacy-components/common/universal-image-actions/universal-image-actions-api-service';
import { BVSamplingCampaignId } from '@models/BVSamplingCampaignId';
import Interval from '@models/Interval';
import { UnifiedMetricsFormattedCampaignId } from '@models/UnifiedMetricsFormattedCampaignId';

export enum RequestContext {
  Touchpoint = 'Touchpoint',
  Facebook = 'Facebook',
  Instagram = 'Instagram',
}

export interface IStatsRequest {
  interval: string;
  ecommerceSessionType: EcommerceSessionType;
  sourceType?: InsightsSourceType[];
  testVariation?: string;
  sourceId?: string;
  displayType?: FanreelDisplayType;
  labels?: string[];
  comparisonInterval?: string;
  requestContext?: RequestContext;
}

export interface IStatsExportRequest extends IStatsRequest {
  sortKey?: SortKey;
  sortDirection?: SortDirection;
}

export interface IItemProductStatsRequest extends IStatsRequest {
  sortKey: SortKey;
  sortDirection: SortDirection;
  limit: number;
  offset: number;
  containerId?: string;
  excludeContainerId?: boolean;
  preferredNetworks?: string[];
}

// Campaign ids can come in a variety of formats, this endpoint will not modify or compose campaign ids, pass in the full campaign id here
export interface ReviewStatsRequest<T extends string = string> {
  interval?: Interval;
  ecommerceSessionType: EcommerceSessionType;
  campaignIds?: T[];
  productServiceIds?: string[];
  sortKey?: SortKey;
  sortDirection?: SortDirection;
  limit?: number;
  offset?: number;
  groupBy?: ReviewStatsGroupBy[];
}

export type SamplingCampaignReviewStatsRequest =
  ReviewStatsRequest<BVSamplingCampaignId>;

export interface ILabelsRequest {
  interval: string;
  sourceType?: string;
  testVariation?: string;
}

export enum CampaignStatsGroupBy {
  SCHEDULE_TIMESTAMP = 'ScheduleTimestamp',
  CLIENT_ID = 'ClientId',
  SOURCE_TYPE = 'SourceType',
  SOURCE_ID = 'SourceId',
  DISPLAY_TYPE = 'DisplayType',
  DEVICE_TYPE = 'DeviceType',
  LOCALE = 'Locale',
  EXPERIMENT_ID = 'ExperimentId',
  VARIATION_ID = 'VariationId',
  ITEM_ID = 'ItemId',
  PRODUCT_SERVICE_ID = 'ProductServiceId',
  CONTAINER_ID = 'ContainerId',
  CURRENCY = 'Currency',
  TIMEZONE = 'Timezone',
  PARENT_PRODUCT_ID = 'ParentProductId',
  CAMPAIGN_ID = 'CampaignId',
  CREATOR_ID = 'CreatorId',
}

export const ReviewStatsGroupBy = {
  SCHEDULE_TIMESTAMP: 'scheduleTimestamp',
  CAMPAIGN_ID: 'campaignId',
  PRODUCT_SERVICE_ID: 'productServiceId',
} as const;

export type ReviewStatsGroupBy =
  (typeof ReviewStatsGroupBy)[keyof typeof ReviewStatsGroupBy];

export interface ICampaignAnalyticsStatsRequest extends IStatsRequest {
  creatorId?: string;
  campaignId?: UnifiedMetricsFormattedCampaignId;
  sortKey?: SortKey;
  sortDirection?: SortDirection;
  limit?: number;
  offset?: number;
  groupBy?: CampaignStatsGroupBy[];
}

export interface IContentAcquisitionResponse {
  acquiredCount: number;
  distributedCount: number;
  taggedCount: number;
}

export interface CampaignAnalyticsStatsResponse
  extends UnifiedMetricsCampaignsStatsResponse {
  currency?: string; // TODO: PD-283870: remove the currency once it's is added to the gateway response
}

export enum FanreelDisplayType {
  HOMEPAGE = 'Homepage',
  CATEGORY = 'Category',
  GALLERY = 'Gallery',
  PRODUCT_DETAIL = 'Pdp',
  CAMPAIGN = 'Campaign',
  INSTASHOP = 'Instashop',
  UNKNOWN = 'Unknown',
}

export enum EcommerceSessionType {
  IMPRESSION = 'Impression',
  HOVER = 'Hover',
  CLICK = 'Click',
  PRODUCT = 'Product',
}

export enum SortKey {
  VIEWS = 'Views',
  INTERACTIVE_VIEWS = 'InteractiveViews',
  EXTERNAL_CLICKS = 'ExternalClicks',
  CLICK_THROUGH_RATE = 'ClickthroughRate',
  FOLLOWERS = 'Followers',
  CONVERSION_RATE = 'ConversionRate',
  REVENUE = 'Revenue',
  PAGE_IMPRESSIONS_NO_CONTENT = 'PageImpressionsNoContent',
}

export enum SortDirection {
  ASC = 'asc',
  DESC = 'desc',
}

export enum InsightsSourceType {
  REEL = 'REEL',
  L2B = 'L2B',
  L2B_V2 = 'L2B_V2',
  WIDGET = 'WIDGET', // Reveal
  TRIBUTARY = 'TRIBUTARY', // Showroom
  DYNAMIC_LANDING_PAGE = 'DYNAMIC_LANDING_PAGE',
  VTV = 'VTV',
  TRENDING_PRODUCTS = 'TRENDING_PRODUCTS',
  IG_STORY = 'IG_STORY',
  IG_PROMOTED_POST = 'IG_PROMOTED_POST',
  IG_SHOPPING = 'IG_SHOPPING',
  FB_POST = 'FB_POST',
  SHORTLINK = 'SHORTLINK',
  SHORTLINK_CMPGN = 'SHORTLINK_CMPGN',
}

export enum MetricsContainerType {
  LIKE_2_BUY = 'Like2Buy',
  FANREEL = 'Fanreel',
  REVEAL = 'Reveal',
  ALL = 'All',
  LANDING = 'DynamicLandingPage',
}

// The generated UnifiedMetricsMetadataResponse type incorrectly expects `currency` to be
// a Currency java type when the gateway is actually only returning an optional string.
export interface UnifiedMetricsMetadataResponseWithCurrencyOverride
  extends Omit<UnifiedMetricsMetadataResponse, 'currency'> {
  configuredTrackingParamGroups?: ITrackingParamGroup[];
  currency?: string;
  isGA4?: boolean;
}

// The response type of the LWS /showroom/stats/metadata endpoint
// Used to determine activation status
export interface IShowroomMetadataResonse {
  hasPosts?: boolean;
}

// The internal model for review stats returned from the metrics gateway
export interface ReviewStat {
  timestamp?: number; // unserialized timestamp, it is serialized below in the dateTimeIdentifier
  dateTimeIdentifier?: string; // date string formatted to MM-DD-YYYY
  campaignId?: string;
  productServiceId?: string; // productIds are saved in UMS as productServiceId as per UMS naming convention
  revenue: number;
}

export interface UnifiedMetricsReviewStatsResponse {
  reviewStats: ReviewStat[];
  totalCount: number;
  currency: string;
}
